import type { ReportFilters } from '~/models/ReportFilters'
import type { ContentDetailsResponse, ReportResponse } from '~/models/Report'
import { storeToRefs } from 'pinia'
import { useAuthStore } from '~/stores/auth'
import { useTime } from '~/composables/useTime'
import { externalClient } from '~/api/client/externalClient'

export default function useReportsApi() {
  const { getDateRange } = useTime()
  const { userId, userOrganization } = storeToRefs(useAuthStore())

  const getReport = (filters: ReportFilters = {}): Promise<ReportResponse> => {
    const activeOrg = userOrganization.value?.number
    const { from, to } = getDateRange(filters.selectedPeriod)

    const requestData = {
      groupId: filters.selectedGroup?.groupId,
      activeOrg,
      userId: userId.value,
      from,
      to,
      limit: filters.limit,
      offset: filters.offset,
      ascending: filters.ascending ?? false,
      content: {
        contentId: null,
        locationId: null,
        subjects: filters.selectedSubjects
      },
    }

    return externalClient.request({
      method: 'POST',
      url: '/reports',
      data: requestData
    }).then(response => response.data)
  }

  const getReportDetails = (contentId: number, filters: ReportFilters = {}): Promise<ContentDetailsResponse> => {
    const activeOrg = userOrganization.value?.number
    const { from, to } = getDateRange(filters.selectedPeriod)

    const params = new URLSearchParams()

    if (filters.selectedGroup?.groupId) {
      params.append('groupId', filters.selectedGroup.groupId)
    }
    if (userId.value) {
      params.append('userId', userId.value)
    }
    if (activeOrg) {
      params.append('activeOrg', String(activeOrg))
    }
    params.append('from', from)
    params.append('to', to)

    return externalClient.request({
      method: 'GET',
      url: `/reports/content/${contentId}?${params.toString()}`
    }).then(response => response.data)
  }

  return {
    getReport,
    getReportDetails,
  }
}
